import React from 'react';
import { ContentfulArticle, ContentfulCompetition, ContentfulHeroCta } from '@jbhi-fi/apis/src/contentful/types';
import { ContentCardCoverImageProps, ContentCardTagProps } from '../../ContentCard';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { ThemeTransformer } from './ThemeTransformer';
import { AssetTransformer } from './AssetTransformer';
import { breakpointSizePx, palette } from '@jbhi-fi/lanyard-ui/themes';
import { CardGridCardProps } from '../../ContentCard/CardGrid/CardGrid';
import { ArticleTransformer } from './ArticleTransformer';
import { CompetitionTransformer } from './CompetitionTransformer';
import { HeroCtaTransformer } from './HeroCtaTransformer';

type CardCompatibleEntry = ContentfulArticle | ContentfulCompetition | ContentfulHeroCta;

export abstract class CardTransformer {
  static get LOADING_CARD_PROPS() {
    return { cover: <></>, isLoading: true };
  }

  static isCompetition = (entry?: CardCompatibleEntry): entry is ContentfulCompetition =>
    entry?.sys?.contentType?.sys?.id === 'competition';

  static isHeroCta = (entry?: CardCompatibleEntry): entry is ContentfulHeroCta =>
    entry?.sys?.contentType?.sys?.id === 'heroCta';

  static isArticle = (entry?: CardCompatibleEntry): entry is ContentfulArticle =>
    entry?.sys?.contentType?.sys?.id === 'article';

  static isLoading = (entry?: CardCompatibleEntry) => {
    if (CardTransformer.isHeroCta(entry)) {
      return !entry?.fields?.theme?.fields;
    }
    return !entry?.fields?.heroImage?.fields;
  };

  static toTagProps = (
    entry?: CardCompatibleEntry,
    { includeSubCategoryHref }: { includeSubCategoryHref?: boolean } = {}
  ): ContentCardTagProps | undefined => {
    if (CardTransformer.isHeroCta(entry)) {
      return undefined;
    }

    if (CardTransformer.isCompetition(entry) && entry?.fields?.stackMemberExclusive) {
      return {
        label: 'MEMBER EXCLUSIVE',
        icon: <Icon name="StackIllustrated" />,
        palette: {
          background: palette.primary.main,
          text: palette.primary.dark,
        },
      };
    }

    if (
      entry?.fields?.subCategoryPage?.fields?.categoryPage?.fields?.heroTheme?.fields ||
      entry?.fields?.subCategoryPage?.fields?.themeColour
    ) {
      const categoryPalette = ThemeTransformer.toContentThemePalette(
        entry?.fields?.subCategoryPage?.fields?.categoryPage?.fields?.heroTheme
      );
      const subCategoryPalette = ThemeTransformer.toContentThemePalette(
        entry?.fields.subCategoryPage?.fields?.themeColour
      );

      return {
        label: entry?.fields?.subCategoryPage?.fields?.heading,
        href: includeSubCategoryHref ? entry?.fields?.subCategoryPage?.fields?.urlSlug : undefined,
        palette: Object.keys(subCategoryPalette)?.length ? subCategoryPalette : categoryPalette,
      };
    }

    return undefined;
  };

  static toHref = (entry?: CardCompatibleEntry): string => {
    if (CardTransformer.isHeroCta(entry)) {
      return entry?.fields?.url ?? '';
    }
    if (CardTransformer.isArticle(entry) || CardTransformer.isCompetition(entry)) {
      return entry?.fields?.subCategoryPage?.fields?.categoryPage?.fields
        ? `/blogs/${entry?.fields?.subCategoryPage?.fields?.categoryPage?.fields?.urlSlug}/${entry?.fields?.urlSlug}`
        : '';
    }
    return '';
  };

  static toCardCoverImageProps = (entry?: CardCompatibleEntry): ContentCardCoverImageProps | undefined => {
    if (CardTransformer.isLoading(entry)) {
      return undefined;
    }
    if (CardTransformer.isHeroCta(entry)) {
      const baseProps = AssetTransformer.toContentfulImageProps(entry?.fields?.image);
      return baseProps ? { ...baseProps, sizes: '160px' } : baseProps;
    }

    const baseProps = AssetTransformer.toContentfulImageProps(entry?.fields?.heroImage);

    return baseProps
      ? {
          ...baseProps,
          sizes: `(max-width: ${breakpointSizePx.md}px) 100vw, 350px`,
          isVideoCard: CardTransformer.isArticle(entry) && Boolean(entry?.fields?.heroVideo?.fields),
        }
      : baseProps;
  };

  static toCardProps = (entry?: CardCompatibleEntry): CardGridCardProps => {
    if (!entry || CardTransformer.isLoading(entry)) {
      return CardTransformer.LOADING_CARD_PROPS;
    }
    if (CardTransformer.isCompetition(entry)) {
      return CompetitionTransformer.toCardProps(entry);
    }
    if (CardTransformer.isArticle(entry)) {
      return ArticleTransformer.toCardProps(entry);
    }
    if (CardTransformer.isHeroCta(entry)) {
      return HeroCtaTransformer.toCardProps(entry);
    }

    const _exhaustiveCheck: never = entry;
    return _exhaustiveCheck;
  };
}
